/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Destination rules", description = "Filter which records can sync to a specific destination, for consent, privacy, or business rules, without changing audience membership.", lastUpdatedDate = new Date(1787788800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Destination rules",
        description: "Filter which records can sync to a specific destination, for consent, privacy, or business rules, without changing audience membership.",
        lastUpdatedDate: "Thu Aug 27 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How destination rules work",
          "depth": 0,
          "id": "how-destination-rules-work"
        }, {
          "value": "Create a destination rule",
          "depth": 0,
          "id": "create-a-destination-rule"
        }, {
          "value": "Preview a rule's impact",
          "depth": 0,
          "id": "preview-a-rules-impact"
        }, {
          "value": "How rules apply to syncs",
          "depth": 0,
          "id": "how-rules-apply-to-syncs"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      h2: "h2",
      ol: "ol",
      li: "li",
      code: "code",
      img: "img",
      hr: "hr",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "parent model"
                  })]
                }), _jsxs("li", {
                  children: ["A ", _jsx(_components.a, {
                    href: "/getting-started/concepts#destinations",
                    children: "destination"
                  }), " to filter"]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Destination rules filter which records can sync to a specific destination, without changing who's in the audience."
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-destination-rules-work",
        children: "How destination rules work"
      }), "\n", _jsxs(_components.p, {
        children: ["Destination rules add filtering on top of an audience at ", _jsx(_components.strong, {
          children: "sync time"
        }), ". They don't change audience membership — only which of those members reach a given destination."]
      }), "\n", _jsx(_components.p, {
        children: "The most common use is enforcing consent. For example, you can exclude users who haven't opted in to email or a specific ad platform, so unconsented records never sync to that destination."
      }), "\n", _jsx(_components.h2, {
        id: "create-a-destination-rule",
        children: "Create a destination rule"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Governance"
          }), ", then open the ", _jsx(_components.strong, {
            children: "Destination rules"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a ", _jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "parent model"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add destination rule"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select a destination."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add filter"
          }), " to define which records are eligible to sync."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The filter builder uses the same conditions as the ", _jsx(_components.a, {
          href: "/customer-studio/usage",
          children: "audience builder"
        }), ", including nested ", _jsx(_components.code, {
          children: "AND"
        }), "/", _jsx(_components.code, {
          children: "OR"
        }), " logic."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/destination-rules/cs-dr-new-rule.png",
          alt: "Defining a destination rule"
        })
      }), "\n", _jsx(_components.h2, {
        id: "preview-a-rules-impact",
        children: "Preview a rule's impact"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you sync, preview how a rule changes an audience's size and membership. The preview is available while you create an audience and from a saved audience's ", _jsx(_components.strong, {
          children: "Definition"
        }), " tab."]
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.strong, {
          children: "Preview a destination"
        }), " dropdown and choose a destination that has a rule. Hightouch applies the rule and updates the preview."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/destination-rules/cs-dr-preview-rules.png",
          alt: "Previewing a destination rule's impact on an audience"
        })
      }), "\n", _jsx(_components.p, {
        children: "The preview doesn't change the audience. It shows only how the rule would affect syncing to that destination."
      }), "\n", _jsx(_components.h2, {
        id: "how-rules-apply-to-syncs",
        children: "How rules apply to syncs"
      }), "\n", _jsx(_components.p, {
        children: "When you sync an audience to a destination that has a rule, Hightouch excludes the rows that don't meet the rule at sync time. Every future sync to that destination applies the rule automatically."
      }), "\n", _jsx(_components.p, {
        children: "Excluded rows still belong to the audience. They're unaffected everywhere except syncing, so they still appear in previews, Insights, and other Customer Studio features."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/syncs",
            children: "Sync audiences"
          }), " to activate an audience once its destination rules are in place."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/subsets",
            children: "Subsets"
          }), " to restrict which records a user can work with, not just which can sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/data-masking",
            children: "Data masking"
          }), " to hide sensitive column values from users who can otherwise see the record."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
